import React, {
  useMemo,
  useState,
} from 'react';

import {
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';

import { FontFamily } from '../../../../constants/fonts';

import MetricCard from '../../components/MetricCard';

import {
  LifecycleCategoryResolutionData,
  LifecycleOverviewCardData,
  LifecycleResolvingPartyData,
  LifecycleStaleTicket,
  LifecycleStatusData,
  LifecycleStatusViewOption,
  TicketingLifecyclePayload,
} from '../../components/types';
import AnalyticsChartCard from '../../components/AnalyticsChartCard';
import StaleTicketsSection from '../../components/StaleTicketsSection';
import VerticalBarChartCard from '../../components/VerticalBarChartCard';
import type { CategoryLookup } from './ticketingAnalyticsKpiApi';
import type { AsyncSectionState } from '../../hooks/useAsyncSection';

interface LifecycleProps {
  /** Per-card async section state — each section has its own loading/error/retry. */
  overviewSection: AsyncSectionState<TicketingLifecyclePayload['overview']>;
  resolvingPartySection: AsyncSectionState<TicketingLifecyclePayload['resolvingParty']>;
  staleTicketsSection: AsyncSectionState<TicketingLifecyclePayload['staleTickets']>;
  resolutionTimePerCategorySection: AsyncSectionState<TicketingLifecyclePayload['resolutionTimePerCategory']>;
  /** Currently selected category chip ids (['all'] or array of category ids) for the Resolution Time by Status card. */
  statusView: string[];
  onStatusViewChange: (views: string[]) => void;
  /** Category chip options (sourced from the CATEGORY filter dropdown, with 'All' prepended). */
  statusViewOptions: LifecycleStatusViewOption[];
  /** Independent loading/data for the category-scoped Resolution Time by Status card. */
  statusByCategoryLoading: boolean;
  statusByCategoryError?: string | null;
  statusByCategoryRows: LifecycleStatusData[];
  categoryLookup?: CategoryLookup;
  /** Retry only the Resolution Time by Status card API. */
  onRetryStatusByCategory?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const formatMetricValue = (
  value: number,
  unit: string,
): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  if (unit === 'hours') {
    return value.toFixed(1);
  }

  return value.toLocaleString();
};

const formatTrendValue = (
  value: number,
  unit: string,
): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  return value.toFixed(1);
};

/**
 * Converts time values to the highest possible unit (days, hours, minutes)
 * Assumes input is in the unit specified, and converts to most readable format
 */
const formatTimeValue = (
  value: number,
  unit: string,
): { value: number; unit: string; label: string } => {
  // Normalize unit to lowercase
  const normalizedUnit = unit?.toLowerCase() ?? 'hours';
  let valueInMinutes = value;
  
  // Convert to minutes first
  if (normalizedUnit === 'hours') {
    valueInMinutes = value * 60;
  } else if (normalizedUnit === 'days') {
    valueInMinutes = value * 24 * 60;
  } else if (normalizedUnit === 'minutes') {
    valueInMinutes = value;
  }
  
  // Convert to highest possible unit
  const minutesPerDay = 24 * 60;
  const minutesPerHour = 60;
  
  if (valueInMinutes >= minutesPerDay) {
    return {
      value: valueInMinutes / minutesPerDay,
      unit: 'days',
      label: 'days',
    };
  } else if (valueInMinutes >= minutesPerHour) {
    return {
      value: valueInMinutes / minutesPerHour,
      unit: 'hours',
      label: 'hrs',
    };
  } else {
    return {
      value: valueInMinutes,
      unit: 'minutes',
      label: 'min',
    };
  }
};

const resolveName = (
  rawId: unknown,
  lookup: Record<number, string> | undefined,
  fallback = '',
): string => {
  const raw = typeof rawId === 'string' ? rawId.trim() : String(rawId ?? '').trim();
  if (!raw) return fallback;

  // Already a human-readable string (not purely numeric) — return as-is
  if (!/^\d+$/.test(raw)) return raw;

  const numId = Number(raw);
  if (lookup && lookup[numId] !== undefined) {
    return lookup[numId];
  }

  // No lookup entry — return the raw string (keeps backward compat)
  return raw;
};

const STATUS_ORDER = [
  'New',
  'In Progress',
  'Escalated to IT',
  'Escalated to TMV',
  'Submitted to ADO',
  'Waiting on User',
  'Closed',
];

const normalizeStatusName = (status: string): string => {
  return status ? status.toLowerCase().trim() : '';
};

/**
 * Maps raw API status names to display labels.
 * Data mapping stays the same; only the label shown to the user changes.
 */
const getStatusDisplayLabel = (status: string): string => {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'waiting on it' || normalized === 'waiting-on-it') return 'Escalated to IT';
  if (normalized === 'waiting on tmv' || normalized === 'waiting-on-tmv') return 'Escalated to TMV';
  return status;
};

const STATUS_ORDER_NORMALIZED = STATUS_ORDER.map(normalizeStatusName);

const Lifecycle: React.FC<
  LifecycleProps
> = ({
  overviewSection,
  resolvingPartySection,
  staleTicketsSection,
  resolutionTimePerCategorySection,
  statusView,
  onStatusViewChange,
  statusViewOptions,
  statusByCategoryLoading,
  statusByCategoryError,
  statusByCategoryRows,
  categoryLookup,
  onRetryStatusByCategory,
  hasActiveFilters = false,
}) => {
  const { width } =
    useWindowDimensions();

  const overviewColumns =
    width <= 880 ? 2 : 6;

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState<string | null>(null);

  const [allStaleTickets, setAllStaleTickets] = useState<LifecycleStaleTicket[]>([]);

  // Sync all stale tickets from API (no pagination needed - all tickets loaded at once)
  React.useEffect(() => {
    const staleData = staleTicketsSection.data;
    if (!staleData?.tickets) return;

    setAllStaleTickets(staleData.tickets);
  }, [staleTicketsSection.data?.tickets]);

  // Load more tickets callback - no-op since all tickets are loaded at once
  const handleLoadMore = React.useCallback(() => {
    // All tickets are already loaded from the initial API call
    // No additional action needed
  }, []);

  const overviewCards =
    useMemo<
      LifecycleOverviewCardData[]
    >(() => {
      return (
        overviewSection.data ?? []
      ).map(item => ({
        label: item.title,
        value: formatMetricValue(
          item.value,
          item.unit,
        ),
        icon: '',
        iconBg: 'transparent',
        iconColor: '#2D2F33',
        subLabel:
          item.unit === 'hours'
            ? 'hrs'
            : undefined,
        trendText: item.trend
          ? formatTrendValue(
              item.trend.value,
              item.trend.unit,
            )
          : undefined,
        trendDirection:
          item.trend?.direction,
        trendImpact:
          item.trend?.impact,
        description:
          item.description,
      }));
    }, [overviewSection.data]);

  const resolvingParty =
    useMemo<
      LifecycleResolvingPartyData
    >(() => {
      return {
        totalResolved:
          resolvingPartySection.data
            ?.totalResolved ?? 0,
        series:
          resolvingPartySection.data
            ?.series ?? [],
      };
    }, [resolvingPartySection.data]);

  const staleTickets =
    useMemo<
      LifecycleStaleTicket[]
    >(() => {
      return (
        staleTicketsSection.data
          ?.tickets ?? []
      );
    }, [staleTicketsSection.data?.tickets]);

  const categoryResolution =
    useMemo<
      LifecycleCategoryResolutionData[]
    >(() => {
      return (
        resolutionTimePerCategorySection.data
          ?.categories ?? []
      ).map(item => ({
        category:
          resolveName(item.category, categoryLookup?.categoryIdToName, item.category),

        valueInHours:
          item.unit === 'days'
            ? item.value * 24
            : item.value,

        displayValue: `${item.value.toFixed(
          1,
        )} ${
          item.unit === 'hours'
            ? 'hrs'
            : 'days'
        }`,

        subCategories: (
          item.subCategories ?? []
        ).map(
          subCategory => ({
            name:
              resolveName(subCategory.name, categoryLookup?.subCategoryIdToName, subCategory.name),

            valueInHours:
              subCategory.unit ===
              'days'
                ? subCategory.value *
                  24
                : subCategory.value,

            displayValue: `${subCategory.value.toFixed(
              1,
            )} ${
              subCategory.unit ===
              'hours'
                ? 'hrs'
                : 'days'
            }`,
          }),
        ),
      }));
    }, [
      resolutionTimePerCategorySection.data
        ?.categories,
      categoryLookup?.categoryIdToName,
      categoryLookup?.subCategoryIdToName,
    ]);

  const selectedCategoryData =
    useMemo(
      () =>
        categoryResolution.find(
          item =>
            item.category ===
            selectedCategory,
        ) ?? null,
      [
        categoryResolution,
        selectedCategory,
      ],
    );

  const RESOLVING_COLORS: Record<string, string> = {
    'Sustain Team': '#005CBC',
    'Navi Team': '#33ABDA',
    'WES Team': '#FF8C09',
    'IT Team': '#8458CC',
  };

  const chartData =
    useMemo(() => {
      if (
        selectedCategoryData
      ) {
        return [
          ...selectedCategoryData.subCategories,
        ]
          .map(item => ({
            label: item.name,
            valueInHours:
              item.valueInHours,
            displayValue:
              item.displayValue,
            clickable: false,
          }))
          .sort(
            (a, b) =>
              b.valueInHours -
              a.valueInHours,
          );
      }

      return [
        ...categoryResolution,
      ]
        .map(item => ({
          label:
            item.category,
          valueInHours:
            item.valueInHours,
          displayValue:
            item.displayValue,
          clickable:
            item
              .subCategories
              .length > 0,
        }))
        .sort(
          (a, b) =>
            b.valueInHours -
            a.valueInHours,
        );
    }, [
      categoryResolution,
      selectedCategoryData,
    ]);

  return (
    <View style={styles.content}>
      {/*
        No blocking "Loading..." text — every card below renders its own
        skeleton via its `loading` prop so the layout stays stable.
      */}
      {/* Per-card errors are rendered inside each card; do not duplicate at page-level. */}

      <View
        style={
          styles.overviewGrid
        }
      >
        {/*
          Overview cards are derived purely from the API response, so while
          loading there is nothing to map over. Render a fixed set of
          placeholder MetricCards to preserve the grid.
        */}
        {overviewSection.loading
          ? Array.from({ length: overviewColumns }).map((_, index) => (
              <View
                key={`overview-skeleton-${index}`}
                style={styles.overviewCardItem}
              >
                <MetricCard loading title="" value="" />
              </View>
            ))
          : overviewCards.map(
          card => (
            <View
              key={card.label}
              style={styles.overviewCardItem}
            >
              <MetricCard
                title={
                  card.label
                }
                value={
                  card.value
                }
                subtitle={
                  card.subLabel
                }
                description={
                  card.description
                }
                trendText={
                  card.trendText
                }
                trendDirection={
                  card.trendDirection
                }
                trendImpact={
                  card.trendImpact ??
                  undefined
                }
              />
            </View>
          ),
        )}
      </View>

        <AnalyticsChartCard
          title="Resolution Time by Status"
          description="Average time in each open status"
          chartType="horizontal-bar"
          chips={statusViewOptions.map(option => ({
            key: option.key,
            label: option.label,
          }))}
          selectedChips={statusView}
          onChipPress={(key) => {
            const current = Array.isArray(statusView) ? statusView : [];
            let next: string[] = [];

            // If user selected 'all', reset to only 'all'.
            if (key === 'all') {
              next = ['all'];
            } else {
              // If 'all' is currently selected, replace it with the new key.
              if (current.includes('all')) {
                next = [key];
              } else {
                // Toggle membership
                if (current.includes(key)) {
                  next = current.filter((k) => k !== key);
                  if (next.length === 0) next = ['all'];
                } else {
                  next = [...current, key];
                }
              }
            }

            onStatusViewChange(next);
          }}
          loading={statusByCategoryLoading}
          error={statusByCategoryError ?? null}
          onRetry={onRetryStatusByCategory}
          hasActiveFilters={hasActiveFilters}
          skeletonRowCount={STATUS_ORDER.length}
          rows={(() => {
            // Transform and deduplicate status rows to ensure unique values
            const transformedRows = statusByCategoryRows.map(item => {
              const timeFormatted = formatTimeValue(
                item.value,
                item.unit,
              );
              // Normalize to hours so all rows share the same unit for bar-width comparison
              const normalizedUnit = (item.unit ?? 'hours').toLowerCase();
              const valueInHours =
                normalizedUnit === 'days' ? item.value * 24
                : normalizedUnit === 'minutes' ? item.value / 60
                : item.value;
              const displayLabel = getStatusDisplayLabel(item.status);
              return {
                label: displayLabel,
                value: valueInHours,
                displayValue: timeFormatted.value,
                unit: timeFormatted.label,
                statusKey: normalizeStatusName(displayLabel), // Use for deduplication
              };
            });

            // Deduplicate by status label (keep first occurrence)
            const uniqueStatusMap = new Map<string, typeof transformedRows[0]>();
            transformedRows.forEach(row => {
              if (!uniqueStatusMap.has(row.statusKey)) {
                uniqueStatusMap.set(row.statusKey, row);
              }
            });

            // Sort by defined status order
            return Array.from(uniqueStatusMap.values()).sort((a, b) => {
              const aIndex = STATUS_ORDER_NORMALIZED.indexOf(a.statusKey);
              const bIndex = STATUS_ORDER_NORMALIZED.indexOf(b.statusKey);
              const aPos = aIndex === -1 ? STATUS_ORDER.length : aIndex;
              const bPos = bIndex === -1 ? STATUS_ORDER.length : bIndex;
              return aPos - bPos;
            }).map(({ statusKey, ...row }) => row); // Remove the temporary statusKey field
          })()}
        />

        <AnalyticsChartCard
          title="Resolving Party"
          description="Share of resolved tickets by team"
          chartType="stacked-bar"
          totalValue={
            resolvingParty.totalResolved
          }
          loading={resolvingPartySection.loading}
          error={resolvingPartySection.error}
          onRetry={resolvingPartySection.retry}
          hasActiveFilters={hasActiveFilters}
          rows={[
            {
              label: 'teams',
              segments:
                resolvingParty.series.map(
                  item => ({
                    label: item.name,
                    value: item.count,
                    percentage: item.percentage,
                    color: RESOLVING_COLORS[item.name] ?? '#005CBC',
                  }),
                ),
            },
          ]}
        />

      {/* Legend removed — labels rendered inside stacked bar segments */}

      <StaleTicketsSection
        title={
          staleTicketsSection.data
            ?.title ??
          'Stale Tickets'
        }
        description={
          staleTicketsSection.data
            ?.description ??
          ''
        }
        tickets={allStaleTickets}
        totalCount={staleTicketsSection.data?.totalCount ?? 0}
        hasNextPage={false}
        onLoadMore={handleLoadMore}
        isLoadingMore={false}
        loading={staleTicketsSection.loading}
        error={staleTicketsSection.error}
        onRetry={staleTicketsSection.retry}
        hasActiveFilters={hasActiveFilters}
      />

      <VerticalBarChartCard
        title={
          selectedCategoryData
            ? `Resolution Time per Category: ${selectedCategoryData.category}`
            : 'Resolution Time per Category'
        }
        subtitle="Average resolution time by category. Select a category to view its sub-categories."
        data={chartData.map(
          item => ({
            label:
              item.label,
            value:
              item.valueInHours,
            valueLabel:
              item.displayValue,
            topLabel:
              item.displayValue,
            clickable:
              item.clickable,
          }),
        )}
        metadataPosition="top"
        loading={resolutionTimePerCategorySection.loading}
        skeletonBarCount={6}
        showBackButton={
          !!selectedCategory
        }
        onBack={() =>
          setSelectedCategory(
            null,
          )
        }
        onBarPress={item => {
          if (
            item.clickable
          ) {
            setSelectedCategory(
              item.label,
            );
          }
        }}
        error={resolutionTimePerCategorySection.error}
        onRetry={resolutionTimePerCategorySection.retry}
        hasActiveFilters={hasActiveFilters}
      />
    </View>
  );
};

const styles =
  StyleSheet.create({
    content: {
      paddingVertical: 12,
      paddingHorizontal: 0,
    },

    metaError: {
      fontSize: 12,
      fontFamily:
        FontFamily.medium,
      color: '#B42318',
      marginTop: 6,
      marginBottom: 8,
    },

    overviewGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 10,
      marginHorizontal: -6,
    },

    overviewCardItem: {
      flex: 1,
      marginHorizontal: 6,
      marginBottom: 14,
      minWidth: 140,
    },

    legendContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 24,
      marginTop: 12,
      marginBottom: 16,
    },

    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },

    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 2,
    },

    legendLabel: {
      fontSize: 12,
      fontFamily: FontFamily.regular,
      color: '#2D2F33',
    },
  });

export default Lifecycle;